import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Check, Copy } from 'lucide-react';
import { toast } from 'sonner';

const LaunchCopy = () => {
  const [copied, setCopied] = useState({});

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied({ ...copied, [id]: true });
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied({ ...copied, [id]: false }), 2000);
  };

  const posts = [
    {
      id: 'linkedin',
      title: 'LinkedIn Post',
      content: `I just mass-applied to 200+ jobs.

Got 3 responses.

That's when I realized: my resume was the problem.

So I spent months building CareerBoost—an AI tool that helps you:

✅ Build resumes that actually get interviews
✅ Track every application in one place
✅ Turn your experience into LinkedIn content

The job market is brutal. The average posting gets 250+ applications.

I also built BizPlanAI for entrepreneurs who need professional business plans but can't afford $2,000 consultants.

And CreditBoost for anyone trying to fix their credit score.

All three tools are live now: https://uplift-dev.preview.emergentagent.com

If you're job hunting, starting a business, or fixing your credit—I built these for you.

What's your biggest career struggle right now? 👇

#JobSearch #Entrepreneurship #CareerAdvice`
    },
    {
      id: 'reddit-resumes',
      title: 'Reddit r/resumes',
      subtitle: 'Title: Built a free AI resume tool after my job search disaster - happy to review resumes',
      content: `Hey r/resumes! I'm Robert, and after applying to 200+ jobs with barely any responses, I realized my resume was the problem.

So I built CareerBoost: https://uplift-dev.preview.emergentagent.com

It uses AI to rewrite your bullets to be achievement-focused and ATS-friendly.

Free to try. But honestly, I'm also here to help—drop your biggest resume challenge in the comments and I'll give you specific advice.`
    },
    {
      id: 'reddit-entrepreneur',
      title: 'Reddit r/Entrepreneur',
      subtitle: 'Title: I built an AI business plan generator after watching my cousin get rejected for an SBA loan',
      content: `My cousin needed a $50K loan to start his catering business. Got rejected because his business plan "wasn't professional enough."

A consultant quoted him $3,000. He didn't have $3,000—that's why he needed the loan.

So I built BizPlanAI: https://uplift-dev.preview.emergentagent.com/bizplanai

AI generates investor-ready business plans in minutes. $49 one-time instead of thousands.

Anyone here gone through the SBA loan process? What was your experience?`
    },
    {
      id: 'reddit-finance',
      title: 'Reddit r/personalfinance',
      subtitle: 'Title: Helped my friend raise his credit score 140 points in 6 months - here\'s the exact playbook',
      content: `My friend went from 580 to 720. Here's what worked:

1. Pulled all 3 credit reports, found 3 errors, disputed them (free)
2. Paid cards down to 10% utilization - score jumped 40 points
3. Got added as authorized user on his mom's old card
4. Stopped applying for new credit

I ended up packaging this into a tool called CreditBoost that automates the analysis: https://uplift-dev.preview.emergentagent.com/creditboost

But the playbook above works for most people even without it. What credit questions do you have?`
    },
    {
      id: 'twitter',
      title: 'Twitter/X Thread',
      content: `🧵 I just mass-applied to 200+ jobs.

Got 3 responses.

Here's what I learned (and what I built to fix it): 👇`
    },
    {
      id: 'twitter-2',
      title: 'Twitter Reply #1',
      content: `The problem wasn't my skills. It was my resume.

Most resumes get rejected in 6 seconds. Mine was probably one of them.`
    },
    {
      id: 'twitter-3',
      title: 'Twitter Reply #2',
      content: `So I built 3 AI tools:

→ CareerBoost (resume builder + job tracker)
→ BizPlanAI (business plans for loans/investors)
→ CreditBoost (credit repair action plans)

All live now: https://uplift-dev.preview.emergentagent.com`
    },
    {
      id: 'twitter-4',
      title: 'Twitter Reply #3',
      content: `If you're struggling with any of these, I built this for you.

DM me if you need help. Seriously.`
    }
  ];

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Launch Copy - Ready to Post</h1>
        <p className="text-slate-600 mb-8">Click the copy button on each one, then paste into the app.</p>
        
        <div className="space-y-6">
          {posts.map((post) => (
            <div key={post.id} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">{post.title}</h2>
                  {post.subtitle && (
                    <p className="text-sm text-indigo-600 mt-1 font-medium">{post.subtitle}</p>
                  )}
                </div>
                <Button
                  onClick={() => copyToClipboard(post.content, post.id)}
                  className={copied[post.id] ? "bg-green-600" : "bg-indigo-600 hover:bg-indigo-700"}
                >
                  {copied[post.id] ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
              <pre className="whitespace-pre-wrap text-slate-700 text-sm bg-slate-50 p-4 rounded-lg font-sans">
                {post.content}
              </pre>
            </div>
          ))}
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Your Links</h2>
          <div className="space-y-2 text-sm">
            <p><strong>Main Site:</strong> https://uplift-dev.preview.emergentagent.com</p>
            <p><strong>CareerBoost:</strong> https://uplift-dev.preview.emergentagent.com/careerboost</p>
            <p><strong>BizPlanAI:</strong> https://uplift-dev.preview.emergentagent.com/bizplanai</p>
            <p><strong>CreditBoost:</strong> https://uplift-dev.preview.emergentagent.com/creditboost</p>
            <p><strong>Launch Page:</strong> https://uplift-dev.preview.emergentagent.com/launch</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LaunchCopy;
